<template>
  <div>
    <div
      class="focus-within:border-primary-400 relative flex flex-row rounded-md border shadow-xs focus-within:border focus-within:ring-0 dark:border"
      :class="label ? 'mt-2' : ''"
    >
      <label
        v-if="label"
        class="capitalize-first absolute -top-2 left-2 z-10 -mt-px inline-block rounded-md bg-white/75 px-1 text-xs font-medium text-gray-900 dark:bg-slate-800/80 dark:text-white"
      >
        {{ label }}
        <span v-if="required" class="text-red-600">*</span>
      </label>

      <button
        ref="trigger"
        type="button"
        class="flex w-full min-w-0 flex-1 flex-row items-center rounded-md border-0 px-3 py-2 text-left focus:ring-0 sm:text-sm"
        :class="
          disabled
            ? 'pointer-events-none cursor-no-drop bg-gray-100 dark:bg-slate-600'
            : 'bg-white dark:bg-slate-800'
        "
        @click="toggleOpen"
      >
        <span
          v-if="selectedOption"
          class="flex-1 truncate text-sm text-gray-900 dark:text-white"
        >
          <span class="mr-1 text-xs text-slate-400">
            {{ selectedOption.groupLabel }} /
          </span>
          {{ selectedOption[displayKey] }}
        </span>
        <span v-else class="flex-1 text-sm text-slate-400">
          {{ placeholder ?? $t('global.aucun_selectionne') }}
        </span>

        <div class="inset-y-0 flex shrink-0 items-center gap-2">
          <button
            v-if="modelValue && !required && !disabled"
            type="button"
            tabindex="-1"
            class="shrink-0 text-xs text-red-600 hover:text-red-800"
            @click.stop="$emit('update:modelValue', null)"
          >
            <FontAwesomeIcon :icon="['far', 'times']" />
          </button>
          <FontAwesomeIcon
            :icon="['fal', 'chevron-down']"
            class="text-slate-400 transition-transform"
            :class="{ 'rotate-180': open }"
          />
        </div>
      </button>
    </div>

    <div v-if="open" class="fixed inset-0 z-[9998]" @click="open = false" />

    <Teleport to="body">
      <div
        v-if="open"
        class="fixed z-[9999] w-80 rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-600 dark:bg-slate-700"
        :style="positionStyle"
      >
        <div class="border-b border-slate-200 p-2 dark:border-slate-600">
          <div
            class="flex items-center gap-2 rounded border border-slate-300 px-2 py-1 dark:border-slate-500"
          >
            <FontAwesomeIcon
              :icon="['fal', 'magnifying-glass']"
              class="text-slate-400"
            />
            <input
              ref="searchInput"
              v-model="search"
              type="text"
              class="flex-1 border-0 bg-transparent text-sm focus:outline-none dark:text-white"
              :placeholder="$t('global.search')"
              @click.stop
            />
            <button
              v-if="search"
              class="text-slate-400 hover:text-slate-600"
              @click.stop="search = ''"
            >
              <FontAwesomeIcon :icon="['fal', 'xmark']" />
            </button>
          </div>
        </div>

        <div class="overflow-y-auto" :style="contentStyle">
          <template v-if="search">
            <button
              v-for="option in filteredOptions"
              :key="option[trackBy]"
              type="button"
              class="hover:bg-primary/10 dark:hover:bg-primary/20 flex w-full flex-col px-3 py-2 text-left"
              :class="{
                'bg-primary/10 dark:bg-primary/20':
                  modelValue === option[trackBy]
              }"
              @click="selectOption(option)"
            >
              <span class="text-xs text-slate-400 dark:text-slate-300">
                {{ option.groupLabel }}
              </span>
              <span class="text-sm text-slate-700 dark:text-white">
                {{ option[displayKey] }}
              </span>
            </button>
            <div
              v-if="!filteredOptions.length"
              class="px-3 py-4 text-center text-sm text-slate-300"
            >
              {{ $t('global.no_result') }}
            </div>
          </template>

          <template v-else>
            <div v-for="group in groups" :key="group.key">
              <button
                type="button"
                class="flex w-full items-center justify-between bg-slate-100 px-3 py-2 text-left text-sm font-semibold text-slate-600 hover:bg-slate-200 dark:bg-slate-600 dark:text-slate-200 dark:hover:bg-slate-500"
                @click.stop="toggleGroup(group.key)"
              >
                <span>{{ group.label }}</span>
                <FontAwesomeIcon
                  :icon="[
                    'fal',
                    openGroups[group.key] ? 'chevron-down' : 'chevron-right'
                  ]"
                  class="text-slate-400"
                />
              </button>

              <template v-if="openGroups[group.key]">
                <button
                  v-for="option in group.options"
                  :key="option[trackBy]"
                  type="button"
                  class="hover:bg-primary/10 dark:hover:bg-primary/20 flex w-full items-center gap-2 px-6 py-1.5 text-left text-sm text-slate-700 dark:text-white"
                  :class="{
                    'bg-primary/10 dark:bg-primary/20 font-medium':
                      modelValue === option[trackBy]
                  }"
                  @click="selectOption({ ...option, groupLabel: group.label })"
                >
                  <FontAwesomeIcon
                    v-if="modelValue === option[trackBy]"
                    :icon="['fal', 'check']"
                    class="text-primary flex-shrink-0"
                  />
                  <span v-else class="w-4 flex-shrink-0" />
                  {{ option[displayKey] }}
                </button>
              </template>
            </div>
          </template>
        </div>
      </div>
    </Teleport>

    <ErrorContainer :error-key="name" :errors="errors" />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'

const DROPDOWN_HEIGHT = 320

export default {
  name: 'SelectGroupedPickerComponent',
  components: { FontAwesomeIcon, ErrorContainer },
  props: {
    modelValue: {
      type: [String, Number],
      default: null
    },
    groups: {
      type: Array,
      required: true
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    displayKey: {
      type: String,
      default: 'name'
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: null
    },
    errors: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      open: false,
      search: '',
      openGroups: {},
      triggerRect: null,
      openAbove: false
    }
  },
  computed: {
    allOptionsFlat() {
      return (this.groups ?? []).flatMap((group) =>
        (group.options ?? []).map((option) => ({
          ...option,
          groupLabel: group.label
        }))
      )
    },
    selectedOption() {
      if (!this.modelValue) return null
      return (
        this.allOptionsFlat.find((o) => o[this.trackBy] === this.modelValue) ??
        null
      )
    },
    filteredOptions() {
      if (!this.search) return []
      const term = this.$h.normalizeSearch(this.search)
      return this.allOptionsFlat.filter(
        (o) =>
          this.$h.normalizeSearch(o[this.displayKey]).includes(term) ||
          this.$h.normalizeSearch(o.groupLabel).includes(term)
      )
    },
    positionStyle() {
      if (!this.triggerRect) return {}
      if (this.openAbove) {
        return {
          bottom: `${window.innerHeight - this.triggerRect.top + 4}px`,
          left: `${this.triggerRect.left}px`
        }
      }
      return {
        top: `${this.triggerRect.bottom + 4}px`,
        left: `${this.triggerRect.left}px`
      }
    },
    contentStyle() {
      if (!this.triggerRect) return {}
      const SEARCH_BAR_HEIGHT = 48
      const available = this.openAbove
        ? this.triggerRect.top - 8
        : window.innerHeight - this.triggerRect.bottom - 8
      return {
        maxHeight: `${Math.min(DROPDOWN_HEIGHT, available) - SEARCH_BAR_HEIGHT}px`
      }
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.$nextTick(() => this.$refs.searchInput?.focus())
      } else {
        this.search = ''
        this.triggerRect = null
      }
    }
  },
  methods: {
    toggleOpen() {
      if (!this.open) {
        this.triggerRect = this.$refs.trigger.getBoundingClientRect()
        const spaceBelow = window.innerHeight - this.triggerRect.bottom
        this.openAbove =
          spaceBelow < DROPDOWN_HEIGHT && this.triggerRect.top > spaceBelow
        this.openGroups = Object.fromEntries(
          this.groups.map((g) => [g.key, true])
        )
      }
      this.open = !this.open
    },
    toggleGroup(key) {
      this.openGroups = { ...this.openGroups, [key]: !this.openGroups[key] }
    },
    selectOption(option) {
      this.$emit('update:modelValue', option[this.trackBy])
      this.open = false
    }
  }
}
</script>
